import {useCallback, useEffect, useRef, useState} from "react";
import LoadingScreen from "./components/LoadingScreen";
import HomePage from "./homepage/HomePage";
import NewsDetailPage from "./news/NewsDetailPage";
import LegalPage from "./legal/LegalPage";

const CDN_BASE_URL = (process.env.REACT_APP_CDN_BASE_URL || process.env.PUBLIC_URL || "").replace(/\/$/, "");
const buildMediaUrl = function (path) {
    if (/^https?:\/\//.test(path)) return path;
    if (!CDN_BASE_URL) return path;
    return `${CDN_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
};
const BG_MUSIC_URL = buildMediaUrl(process.env.REACT_APP_BG_MUSIC_URL || "/audio/bg-music.mp3");
const HERO_VIDEO_LOAD_TIMEOUT_MS = 30000;
const HERO_VIDEO_PROGRESS_FINISH_MS = 700;

function getRoute() {
    const hash = window.location.hash || "";
    const newsMatch = hash.match(/^#\/newsdetail\/([^/?#]+)/);
    const legalMatch = hash.match(/^#\/(user-agreement|privacy-policy|contact-us)/);

    if (newsMatch) {
        return {
            name: "newsDetail",
            id: decodeURIComponent(newsMatch[1])
        };
    }

    if (legalMatch) {
        return {
            name: "legal",
            type: legalMatch[1]
        };
    }

    return {name: "home"};
}

export default function App() {
    const [route, setRoute] = useState(getRoute);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [isHeroVideoReady, setIsHeroVideoReady] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isMusicOn, setIsMusicOn] = useState(false);
    const bgMusicRef = useRef(null);
    const loadingProgressRef = useRef(0);

    const moveLoadingProgressForward = useCallback(function (progress) {
        const nextProgress = Math.max(0, Math.min(100, Math.round(progress)));
        if (nextProgress <= loadingProgressRef.current) return;

        loadingProgressRef.current = nextProgress;
        setLoadingProgress(nextProgress);
    }, []);

    useEffect(() => {
        const handleHashChange = function () {
            const nextRoute = getRoute();
            setRoute(nextRoute);

            if (nextRoute.name !== "home") {
                window.scrollTo(0, 0);
            }
        };

        window.addEventListener("hashchange", handleHashChange);

        return () => {
            window.removeEventListener("hashchange", handleHashChange);
        };
    }, []);

    useEffect(() => {
        if (!isLoading || route.name !== "home" || isHeroVideoReady) return undefined;

        const timerId = window.setTimeout(function () {
            setIsHeroVideoReady(true);
        }, HERO_VIDEO_LOAD_TIMEOUT_MS);

        return () => window.clearTimeout(timerId);
    }, [isHeroVideoReady, isLoading, route.name]);

    useEffect(() => {
        if (!isLoading || (route.name === "home" && !isHeroVideoReady)) return undefined;

        if (route.name !== "home") {
            moveLoadingProgressForward(100);
            return undefined;
        }

        const initialProgress = loadingProgressRef.current;
        const startedAt = window.performance.now();
        let frameId = null;

        const finishProgress = function (currentTime) {
            const elapsedRatio = Math.min(1, (currentTime - startedAt) / HERO_VIDEO_PROGRESS_FINISH_MS);
            const easedRatio = 1 - Math.pow(1 - elapsedRatio, 3);
            moveLoadingProgressForward(initialProgress + ((100 - initialProgress) * easedRatio));

            if (elapsedRatio < 1) {
                frameId = window.requestAnimationFrame(finishProgress);
                return;
            }

        };

        frameId = window.requestAnimationFrame(finishProgress);

        return () => {
            if (frameId !== null) window.cancelAnimationFrame(frameId);
        };
    }, [isHeroVideoReady, isLoading, moveLoadingProgressForward, route.name]);

    useEffect(() => {
        const bgMusic = bgMusicRef.current;
        if (isLoading || !bgMusic) return undefined;

        bgMusic.volume = 0.45;
        const syncMusicState = function () {
            setIsMusicOn(!bgMusic.paused);
        };
        const tryPlayMusic = function () {
            bgMusic.play().then(function () {
                setIsMusicOn(true);
                window.removeEventListener("click", tryPlayMusic);
                window.removeEventListener("keydown", tryPlayMusic);
            }).catch(function () {
                setIsMusicOn(false);
            });
        };

        bgMusic.addEventListener("play", syncMusicState);
        bgMusic.addEventListener("pause", syncMusicState);
        tryPlayMusic();
        window.addEventListener("click", tryPlayMusic, {once: true});
        window.addEventListener("keydown", tryPlayMusic, {once: true});

        return () => {
            bgMusic.removeEventListener("play", syncMusicState);
            bgMusic.removeEventListener("pause", syncMusicState);
            window.removeEventListener("click", tryPlayMusic);
            window.removeEventListener("keydown", tryPlayMusic);
        };
    }, [isLoading]);

    const handleMusicToggle = useCallback(function () {
        const bgMusic = bgMusicRef.current;
        if (!bgMusic) return;

        if (bgMusic.paused) {
            bgMusic.play().catch(function () {
                setIsMusicOn(false);
            });
        } else {
            bgMusic.pause();
        }
    }, []);

    const handleMusicPause = useCallback(function () {
        const bgMusic = bgMusicRef.current;
        const wasPlaying = Boolean(bgMusic && !bgMusic.paused);
        if (wasPlaying) bgMusic.pause();
        return wasPlaying;
    }, []);

    const handleMusicPlay = useCallback(function () {
        const bgMusic = bgMusicRef.current;
        if (!bgMusic || !bgMusic.paused) return;

        bgMusic.play().catch(function () {
            setIsMusicOn(false);
        });
    }, []);

    const handleHeroVideoReady = useCallback(function () {
        setIsHeroVideoReady(true);
    }, []);

    const handleHeroVideoProgress = useCallback(function (progress) {
        if (!Number.isFinite(progress)) return;
        const nextProgress = Math.max(0, Math.min(99, Math.round(progress)));
        moveLoadingProgressForward(nextProgress);
    }, [moveLoadingProgressForward]);

    const handleLoadingComplete = useCallback(function () {
        setIsLoading(false);
    }, []);

    const musicProps = {
        isMusicOn,
        onMusicToggle: handleMusicToggle
    };
    let page = <HomePage {...musicProps} onMusicPause={handleMusicPause} onMusicPlay={handleMusicPlay} onHeroVideoReady={handleHeroVideoReady} onHeroVideoProgress={handleHeroVideoProgress} />;

    if (route.name === "newsDetail") {
        page = <NewsDetailPage id={route.id} {...musicProps} />;
    }

    if (route.name === "legal") {
        page = <LegalPage type={route.type} {...musicProps} />;
    }

    return (
        <>
            {page}
            <audio ref={bgMusicRef} id="bgMusic" src={BG_MUSIC_URL} loop preload="auto"></audio>
            <div className="development-notice" aria-hidden="true">*游戏研发中，非最终品质。</div>
            {isLoading ? <LoadingScreen progress={loadingProgress} onComplete={handleLoadingComplete} /> : null}
        </>
    );
}
